import { useCallback, useRef, useState, type MouseEvent } from 'react';
import { useParams } from 'react-router-dom';
import type { WaapPlanFeatures } from '@/api/waap';
import { useWaapEntitlements } from './waap-entitlements';
import { WaapUpgradeDialog } from './WaapUpgradeDialog';

/**
 * Action-level plan gate for the WAAP console. Every feature stays fully
 * browsable; only the primary mutating action (create / enable) is gated. Wrap a
 * button/toggle handler with `gate(feature, label, run)` — if the active plan
 * includes `feature` the original handler runs, otherwise the upgrade popup opens
 * instead. Render `upgradeDialog` once in the component.
 *
 * Service id is read from the route (like the rest of the console), so no prop
 * threading into deeply-nested list/toggle sub-components is needed.
 */
export function useWaapUpgradeGate() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const { features } = useWaapEntitlements(serviceId);
  const [gated, setGated] = useState<string | null>(null);

  // Keep the latest entitlements in a ref so `gate` can stay referentially stable
  // (safe to capture inside a useMemo'd column def / handler) yet always evaluate
  // against the current plan rather than the plan at closure-creation time.
  const featuresRef = useRef(features);
  featuresRef.current = features;

  const gate = useCallback(
    <E,>(feature: keyof WaapPlanFeatures, label: string, run: (e?: E) => void) =>
      (e?: E) => {
        if (featuresRef.current[feature]) {
          run(e);
          return;
        }
        // Block the underlying action (e.g. a controlled toggle) and prompt upgrade.
        (e as unknown as MouseEvent | undefined)?.preventDefault?.();
        setGated(label);
      },
    [],
  );

  const upgradeDialog = (
    <WaapUpgradeDialog
      open={gated !== null}
      onOpenChange={(o) => !o && setGated(null)}
      feature={gated ?? ''}
    />
  );

  return { gate, upgradeDialog };
}
